import { useState } from "react";
import {
  Alert,
  Modal,
  Tag,
  Tooltip,
  Button,
  Space,
  Popconfirm,
  Dropdown,
} from "antd";
import type { MenuProps } from "antd";
import {
  CopyOutlined,
  FileTextOutlined,
  CheckOutlined,
  PlusOutlined,
  LockOutlined,
  ToolOutlined,
  ClearOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  PhoneOutlined,
  HomeOutlined,
  InfoCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  DollarOutlined,
  MoonOutlined,
} from "@ant-design/icons";
import type {
  CalendarCellData,
  CalendarEventRef,
  Property,
} from "./useCalendarioLogic";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { formatPlataforma } from "../../utils/plataforma.utils";
import { formatPropertyName } from "../../utils/propertyDisplay";
import { formatPhoneDisplay } from "../../utils/formatPhone";
import EstadiaCodeTag from "../../Components/EstadiaCodeTag";
import AseoCodeTag from "../../Components/AseoCodeTag";
import { formatEstadiaCode } from "../../utils/estadiaCode";
// Bridge antd estático: los métodos `Modal.x` / `message.x` /
// `notification.x` importados directo de "antd" no funcionan en
// v5 + React 19 (root React huérfano fuera del ConfigProvider).
// Round 2026-05-01 — sweep general post-fix /sueldos.
import { copiarInstruccionesDePropiedad } from "../../utils/mensajeInicial";
import RecalcBookingButton from "../../Components/RecalcBookingButton";
import RegenerarAseoButton from "../../Components/RegenerarAseoButton";

/**
 * Formatea un ISO o date-only a "DD MMM YYYY" en español (ej: "25 abr 2026").
 * Null-safe: retorna "—" si el valor no es parseable.
 */
function fmtDate(d: string | undefined | null): string {
  if (!d) return "—";
  const x = dayjs(d);
  if (!x.isValid()) return "—";
  return x.format("DD MMM YYYY");
}

/** Formatea un ISO datetime a "HH:mm". Para hora de INICIO. */
function fmtTime(d: string | undefined | null): string {
  if (!d) return "—";
  const x = dayjs(d);
  if (!x.isValid()) return "—";
  return x.format("HH:mm");
}

/**
 * Formatea un ISO datetime a "HH:mm" para hora de TÉRMINO.
 * Normaliza los bordes de tercio al "último minuto ocupado" (convención
 * docs/calendario-logica.md §1.2):
 *   - 11:00 → 10:59 (fin de morning)
 *   - 15:00 → 14:59 (fin de mid)
 *   - 00:00 / 24:00 → 23:59 (fin de evening)
 * Así la UI nunca muestra "hasta las 11:00" (que pertenece a mid) como
 * hora de término — evita la ambigüedad que reporta el usuario: "dice
 * 11:00 pero debería ser 10:59".
 */
function fmtTimeEnd(d: string | undefined | null): string {
  if (!d) return "—";
  const x = dayjs(d);
  if (!x.isValid()) return "—";
  const hhmm = x.format("HH:mm");
  if (hhmm === "11:00") return "10:59";
  if (hhmm === "15:00") return "14:59";
  if (hhmm === "00:00" || hhmm === "24:00") return "23:59";
  return hhmm;
}

/**
 * Calcula la duración del evento en un texto amigable.
 * - Mismo día (<24h): "4h 30m"
 * - ≥1 día: "X noches"
 */
function durationLabel(
  start: string | undefined | null,
  end: string | undefined | null,
): string {
  if (!start || !end) return "";
  const s = dayjs(start);
  const e = dayjs(end);
  if (!s.isValid() || !e.isValid()) return "";
  const dayDiff = e.startOf("day").diff(s.startOf("day"), "day");
  if (dayDiff >= 1) {
    return `${dayDiff} ${dayDiff === 1 ? "noche" : "noches"}`;
  }
  const minutes = e.diff(s, "minute");
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h`;
  if (mins > 0) return `${mins}m`;
  return "";
}

type Props = {
  open: boolean;
  cell: CalendarCellData | null;
  propiedades: Property[];
  onClose: () => void;

  onEditEvent?: (ev: CalendarEventRef, slots: Slot[]) => void;
  /**
   * Callback de borrado. `opts.preserveCleaning=true` solo aplica para
   * `kind="booking"`: desvincula el aseo AUTO (reserva_id=null) en vez
   * de borrarlo — se usa cuando el admin quiere conservar el aseo aunque
   * elimine la reserva (ej. aseo ya ejecutado o ya asignado).
   */
  onDeleteEvent?: (
    ev: CalendarEventRef,
    slots: Slot[],
    opts?: { preserveCleaning?: boolean },
  ) => void;

  canEditDelete?: (ev: CalendarEventRef) => boolean;
  hideCleaningDetails?: boolean;
  /**
   * Oculta el campo "Notas internas" (= `bloqueos.notas` o `aseos.notas`)
   * en las tarjetas de bloqueo/mantención. Ese campo es exclusivo de
   * admin — el cliente sólo ve `razon` (público). Ver
   * docs/modulo-calendario.md §"Visibilidad de campos por rol".
   */
  hideInternalNotes?: boolean;
  /**
   * Oculta los botones "Copiar información" y "Copiar instrucciones".
   * Usado en la vista del cliente — el propietario no debe acceder a los
   * datos del huésped ni al mensaje inicial configurado por la empresa.
   */
  hideCopyActions?: boolean;
  /**
   * Oculta el botón "Ver en Finanzas". Debe pasarse true en la vista
   * cliente — navegar a `/finanzas-empresa` desde un cliente rompe la
   * pantalla porque esa página asume permisos de admin y las APIs
   * devuelven 401 (blank screen).
   */
  hideFinancesAction?: boolean;
  /**
   * Oculta el campo "Teléfono" del huésped/arrendatario en el detalle
   * de la reserva. Debe pasarse true en la vista cliente — el
   * propietario no debe acceder al número de contacto del huésped
   * (solo nombre). El nombre se mantiene visible para que el cliente
   * sepa quién está alojado en su propiedad.
   */
  hideGuestPhone?: boolean;
  /**
   * Si es true, el día todavía acepta iniciar un bloqueo cliente (tercio
   * evening libre + mañana del día siguiente libre). Ver
   * `canStartClientBlockOn` en calendarioClienteMonth.tsx. Cuando está
   * activo, renderizamos un banner invitando a crear un nuevo bloqueo
   * que empezará ese mismo día a las 15:00.
   */
  canCreateBlockOnDay?: boolean;
  /**
   * Callback invocado cuando el cliente acepta crear el bloqueo desde
   * este modal (click en el banner). Recibe el ISO del día actual.
   */
  onCreateBlockFromDay?: (iso: string) => void;
  /**
   * Rol del usuario actual. Si es admin/superadmin se muestra el botón
   * "Recalcular finanzas" que reprocesa el último JSON de Stays guardado
   * para esta reserva. Ver `RecalcBookingButton` y
   * docs/modulo-stays.md §"Recálculo manual desde UI".
   */
  userRole?: string | string[] | null;
  /**
   * Callback invocado tras un recálculo financiero exitoso, para que el
   * caller refresque sus datos del calendario.
   */
  onRecalculated?: () => void;
};

type Slot = "morning" | "mid" | "evening";

type GroupedEvent = {
  base: CalendarEventRef;
  slots: Slot[];
};

// Rangos horarios de cada tercio según docs/calendario-logica.md §1.1.
// Intervalos medio-abiertos [a, b): los bordes 11:00 y 15:00 pertenecen
// al tercio SIGUIENTE, por eso el "fin" humano se muestra como el último
// minuto ocupado (10:59, 14:59, 23:59).
function slotLabel(slots: Slot[]) {
  if (slots.length === 3) return "Todo el día";
  if (slots.includes("morning") && slots.includes("evening"))
    return "Mañana y Tarde";
  if (slots.includes("morning")) return "Mañana (00:00 – 10:59)";
  if (slots.includes("mid")) return "Mediodía (11:00 – 14:59)";
  return "Tarde (15:00 – 23:59)";
}

/**
 * Campo clave/valor con estilo visual consistente.
 * Usado dentro de las cards de cada evento.
 */
function Field({
  icon,
  label,
  children,
  highlight,
}: {
  icon?: React.ReactNode;
  label: string;
  children: React.ReactNode;
  highlight?: boolean;
}) {
  return (
    <div
      className={`cal-detail-field${highlight ? " cal-detail-field--highlight" : ""}`}
    >
      <div className="cal-detail-field__label">
        {icon ? <span className="cal-detail-field__icon">{icon}</span> : null}
        {label}
      </div>
      <div className="cal-detail-field__value">{children}</div>
    </div>
  );
}

/**
 * Botón "Eliminar" del arriendo con dos opciones:
 *   - "Eliminar arriendo y aseo" (default, cascade completo — incluye
 *     bloqueo AUTO de salida tardía si lo hubiera y "otros cargos").
 *   - "Eliminar solo arriendo, conservar aseo" (desvincula el aseo:
 *     `aseos.reserva_id = null`). Útil cuando el aseo ya se ejecutó o
 *     ya está asignado a un aseador y NO se quiere perder ese registro.
 *
 * El bloqueo AUTO de salida tardía y los gastos se borran SIEMPRE junto
 * con el arriendo — no tiene sentido conservarlos sin su reserva.
 * Ver docs/calendario-cascada-arriendos.md §2 "Eliminación del booking".
 */
function BookingDeleteDropdown({
  onDeleteWithCleaning,
  onDeleteOnly,
}: {
  onDeleteWithCleaning: () => void;
  onDeleteOnly: () => void;
}) {
  const [confirmKind, setConfirmKind] = useState<null | "full" | "only">(null);

  // Menú con ambas opciones explícitas. Antes era un Dropdown.Button (split)
  // donde el click principal disparaba "full" y el menú sólo tenía "only";
  // esto provocaba que el click se ambiguara entre "ejecutar full" y "abrir
  // menú", y en la práctica ningún path llegaba a setConfirmKind. Ahora el
  // botón único abre el menú; el usuario elige explícitamente la variante.
  const items: MenuProps["items"] = [
    {
      key: "full",
      label: "Eliminar arriendo y aseo",
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => setConfirmKind("full"),
    },
    {
      key: "only",
      label: "Eliminar solo arriendo (conservar aseo)",
      icon: <ClearOutlined />,
      onClick: () => setConfirmKind("only"),
    },
  ];

  return (
    <>
      <Dropdown menu={{ items }} trigger={["click"]}>
        <Button size="small" danger icon={<DeleteOutlined />}>
          Eliminar
        </Button>
      </Dropdown>

      <Modal
        open={confirmKind !== null}
        onCancel={() => setConfirmKind(null)}
        onOk={() => {
          if (confirmKind === "full") onDeleteWithCleaning();
          else if (confirmKind === "only") onDeleteOnly();
          setConfirmKind(null);
        }}
        okText={
          confirmKind === "only"
            ? "Eliminar solo arriendo"
            : "Eliminar arriendo y aseo"
        }
        cancelText="Cancelar"
        okButtonProps={{ danger: true }}
        title={
          confirmKind === "only"
            ? "Eliminar solo arriendo — conservar aseo"
            : "Eliminar arriendo y aseo"
        }
        destroyOnClose
      >
        {confirmKind === "only" ? (
          <div>
            <p>
              Se va a eliminar <strong>solo la reserva</strong>. El aseo AUTO
              asociado <strong>se conservará como aseo independiente</strong>{" "}
              (queda sin vínculo al arriendo, se puede editar o completar
              normalmente).
            </p>
            <p style={{ marginBottom: 0 }}>
              También se eliminan: el bloqueo de salida tardía (si existe) y
              los cargos adicionales linkeados a la reserva.
            </p>
          </div>
        ) : (
          <div>
            <p>
              Se va a eliminar la reserva <strong>y también el aseo AUTO</strong>{" "}
              vinculado a ella. Si el aseo ya estaba asignado a un aseador o
              tenía observaciones cargadas, esa información se pierde.
            </p>
            <p style={{ marginBottom: 0 }}>
              Si prefieres conservar el aseo, cancela y elige{" "}
              <em>"Eliminar solo arriendo (conservar aseo)"</em> desde el
              desplegable del botón Eliminar.
            </p>
          </div>
        )}
      </Modal>
    </>
  );
}

export default function BookingDetailModal({
  open,
  propiedades,
  cell,
  onClose,
  onEditEvent,
  onDeleteEvent,
  canEditDelete,
  hideCleaningDetails,
  hideInternalNotes,
  hideCopyActions,
  hideFinancesAction,
  hideGuestPhone,
  canCreateBlockOnDay,
  onCreateBlockFromDay,
  userRole,
  onRecalculated,
}: Props) {
  const navigate = useNavigate();
  const [copiedInfo, setCopiedInfo] = useState(false);
  const [copiedInst, setCopiedInst] = useState(false);
  if (!cell) return null;

  const grouped = new Map<string, GroupedEvent>();

  const push = (ev: CalendarEventRef, slot: Slot) => {
    const key =
      ev.kind === "booking"
        ? `booking:${
            ev.bookingDbId ??
            ev.bookingId ??
            `${ev.plataforma ?? ""}|${ev.codigoPlataforma ?? ""}|${ev.ingreso ?? ""}|${ev.salida ?? ""}|${ev.huesped ?? ""}`
          }`
        : ev.kind === "cleaning"
          ? `cleaning:${ev.cleaningId}`
          : `${ev.kind}:${ev.id}`;

    const g = grouped.get(key);
    if (!g) {
      grouped.set(key, { base: ev, slots: [slot] });
    } else if (!g.slots.includes(slot)) {
      g.slots.push(slot);
    }
  };

  (cell.thirds.morning.events ?? []).forEach((e) => push(e, "morning"));
  (cell.thirds.mid.events ?? []).forEach((e) => push(e, "mid"));
  (cell.thirds.evening.events ?? []).forEach((e) => push(e, "evening"));

  // Aseo AUTO vinculado (round 2026-07-11): si un arriendo de este día tiene un
  // aseo vinculado que NO cae en la celda clickeada (día NO compartido, ej. el
  // arriendo intermedio y su aseo en el check-out de otro día), lo inyectamos
  // como tarjeta de aseo EDITABLE. La key "cleaning:{id}" dedupea: si el aseo
  // comparte el día con el arriendo, ya está en `grouped` y no se duplica.
  // Solo admin (hideCleaningDetails=false) — el cliente no ve detalles de aseo.
  if (!hideCleaningDetails) {
    for (const g of Array.from(grouped.values())) {
      if (g.base.kind !== "booking") continue;
      const lc = g.base.linkedCleaning;
      if (!lc || lc.kind !== "cleaning") continue;
      const key = `cleaning:${lc.cleaningId}`;
      if (grouped.has(key)) continue;
      // Slot informativo derivado de la hora de inicio del aseo (para el tag
      // "Mañana/Mediodía/Tarde"). El editor recalcula el tercio real al abrir.
      const startMin = lc.start
        ? dayjs(lc.start).hour() * 60 + dayjs(lc.start).minute()
        : 660;
      const slot: Slot =
        startMin < 660 ? "morning" : startMin < 900 ? "mid" : "evening";
      grouped.set(key, { base: lc, slots: [slot] });
    }
  }

  const order: Record<Slot, number> = { morning: 0, mid: 1, evening: 2 };

  const timeline = Array.from(grouped.values()).sort(
    (a, b) =>
      Math.min(...a.slots.map((s) => order[s])) -
      Math.min(...b.slots.map((s) => order[s])),
  );

  const copyInfo = (ev: CalendarEventRef) => {
    const e = ev as any;
    const lines = [
      `Huésped: ${e.huesped ?? "-"}`,
      `Teléfono: ${formatPhoneDisplay(e.telefono) || "-"}`,
      `Plataforma: ${formatPlataforma(e.plataforma)}`,
      `Código: ${e.codigoPlataforma ?? e.bookingId ?? "-"}`,
      `Entrada: ${fmtDate(e.ingreso)}`,
      `Salida: ${fmtDate(e.salida)}`,
      `Total: ${e.reservaTotalClp != null ? `$${Number(e.reservaTotalClp).toLocaleString("es-CL")}` : "-"}`,
      `Propiedad: ${cell?.nombrePropiedad ?? "-"}`,
    ];
    navigator.clipboard.writeText(lines.join("\n"));
    setCopiedInfo(true);
    setTimeout(() => setCopiedInfo(false), 2000);
  };

  const copyInstructions = async () => {
    // Mismo helper que el ítem «Copiar instrucciones» del menú de la grilla:
    // avisa el éxito o el motivo exacto del fallo (sin mensaje / no se pudo
    // cargar / portapapeles bloqueado). El botón solo cambia a «¡Copiado!»
    // cuando de verdad quedó copiado.
    const r = await copiarInstruccionesDePropiedad(cell?.idPropiedad, propiedades);
    if (r !== "copiado") return;
    setCopiedInst(true);
    setTimeout(() => setCopiedInst(false), 2000);
  };

  const goToFinanzasArriendos = (booking: CalendarEventRef) => {
    const b = booking as any;

    // Rango de fechas que cubre el arriendo (con margen) para que el filtro
    // global de fecha de /arriendos no lo excluya por la regla de mes
    // contable. Usamos ingreso/salida del evento si están; si no, el día
    // de la celda ±5.
    const ingreso = b.ingreso ? dayjs(b.ingreso) : dayjs(cell.date);
    const salida = b.salida ? dayjs(b.salida) : dayjs(cell.date);
    const from = ingreso.subtract(5, "day").format("YYYY-MM-DD");
    const to = salida.add(5, "day").format("YYYY-MM-DD");

    const params = new URLSearchParams({ from, to });
    // `bookingId` = id del booking en DB → la tabla de /arriendos prefiltra
    // para mostrar SOLO ese arriendo. Si por algún motivo el evento no trae
    // el id (caso raro), caemos al filtro por propiedad como antes.
    if (b.bookingDbId != null) {
      params.set("bookingId", String(b.bookingDbId));
    } else if (cell.idPropiedad != null) {
      params.set("propiedadId", String(cell.idPropiedad));
    }

    navigate(`/arriendos?${params.toString()}`);
    onClose();
  };

  const friendlyDate = fmtDate(cell.date);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width="min(95vw, 720px)"
      title={
        <div className="cal-detail-modal-title">
          <CalendarOutlined />
          <span className="cal-detail-modal-title__main">
            {friendlyDate}
          </span>
          {cell.nombrePropiedad ? (
            <span className="cal-detail-modal-title__sub">
              <HomeOutlined /> {formatPropertyName(cell.nombrePropiedad)}
            </span>
          ) : null}
        </div>
      }
    >
      {/*
       * Banner de creación: aparece cuando el día todavía acepta iniciar
       * un bloqueo cliente (15:00 → 10:59 día siguiente) porque su tercio
       * evening está libre y la mañana del día siguiente también. Esto
       * cubre el caso "día con booking saliendo / aseo en mid": el día
       * NO está 100% ocupado aunque haya eventos. Ver
       * docs/calendario-logica.md §1 y canStartClientBlockOn.
       */}
      {canCreateBlockOnDay && onCreateBlockFromDay ? (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
          message="Este día acepta un nuevo bloqueo"
          description={
            <span>
              Puedes crear un bloqueo que empiece a las{" "}
              <strong>15:00 del {fmtDate(cell.date)}</strong> y termine a las{" "}
              <strong>
                10:59 del día{" "}
                {fmtDate(dayjs(cell.date).add(1, "day").format("YYYY-MM-DD"))}
              </strong>
              . Al abrir el modal podrás extender el rango — se validará solo
              el tope horario con otros eventos, no el día en sí.
            </span>
          }
          action={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => onCreateBlockFromDay(cell.date)}
            >
              Crear bloqueo
            </Button>
          }
        />
      ) : null}

      {timeline.length === 0 && (
        <div className="cal-detail-empty">
          <CalendarOutlined />
          <div>
            <div className="cal-detail-empty__title">Día sin eventos</div>
            <div className="cal-detail-empty__sub">
              No hay arriendos, aseos ni bloqueos para este día.
            </div>
          </div>
        </div>
      )}

      {timeline.map((group, idx) => {
        const ev = group.base;
        const slotsSorted = [...group.slots].sort(
          (a, b) => order[a] - order[b],
        );

        const wantsActions = !!onEditEvent || !!onDeleteEvent;
        const allowedByRule = canEditDelete ? canEditDelete(ev) : true;
        const showActions = wantsActions && allowedByRule;

        /* ===============================
           BOOKING — tarjeta azul con resumen financiero y datos del huésped
           =============================== */
        if (ev.kind === "booking") {
          const slots = slotsSorted;

          let phase: string;
          if (slots.length === 1 && slots[0] === "morning") {
            phase = "Check-out";
          } else if (slots.length === 1 && slots[0] === "evening") {
            phase = "Check-in";
          } else {
            phase = "Estadía en curso";
          }

          return (
            <div
              key={`booking-${idx}`}
              className="cal-detail-card cal-detail-card--booking"
            >
              <div className="cal-detail-card__header">
                <div className="cal-detail-card__title">
                  <CalendarOutlined />
                  <span>Arriendo</span>
                </div>
                <div className="cal-detail-card__tags">
                  {/* Nº de estadía = id interno único de la reserva
                      (reservas.id). Es el MISMO número que el aseo vinculado
                      muestra como "Estadía #N", para poder cruzarlos aunque
                      caigan en días distintos. Es estable: no cambia aunque se
                      editen las fechas del arriendo. Round 2026-07-15. */}
                  <EstadiaCodeTag id={ev.bookingDbId} />
                  <Tag color="blue">{phase}</Tag>
                  <Tag>{slotLabel(slots)}</Tag>
                </div>
              </div>

              <div className="cal-detail-card__body">
                <div className="cal-detail-grid">
                  <Field
                    icon={<CalendarOutlined />}
                    label="Check-in"
                    highlight
                  >
                    {fmtDate(ev.ingreso)}
                    <span className="cal-detail-field__meta">· 15:00</span>
                  </Field>
                  <Field
                    icon={<CalendarOutlined />}
                    label="Check-out"
                    highlight
                  >
                    {fmtDate(ev.salida)}
                    <span className="cal-detail-field__meta">· 10:59</span>
                  </Field>
                </div>

                <div className="cal-detail-grid">
                  <Field label="Plataforma">
                    {formatPlataforma(ev.plataforma)}
                  </Field>
                  <Field label="Código plataforma">
                    {ev.codigoPlataforma ?? "—"}
                  </Field>
                </div>

                {/*
                  «Ref. plataforma» = código corto de la plataforma
                  (`id_reservac`, formato JS11J). Se muestra SOLO cuando la
                  reserva vino de Stays/Airbnb/Booking; permite cruzarla con el
                  panel /stays-webhooks. El identificador universal de toda
                  reserva es «Estadía #N». El AF-… (numero_corto) quedó legacy
                  — round 2026-07-20.
                */}
                {(ev.idReservaStays || ev.fechaCreacion) && (
                  <div className="cal-detail-grid">
                    {ev.idReservaStays && (
                      <Field label="Ref. plataforma">
                        <Tooltip title="Código corto de la plataforma (Stays/Airbnb/Booking) — copia este código en /stays-webhooks (campo Buscar) para encontrar el JSON original.">
                          <strong>{ev.idReservaStays}</strong>
                        </Tooltip>
                      </Field>
                    )}
                    {ev.fechaCreacion && (
                      <Field label="Creada">
                        {dayjs(ev.fechaCreacion).format("DD MMM YYYY · HH:mm")}
                      </Field>
                    )}
                  </div>
                )}

                {(ev.reservaTotalClp != null || ev.precioPorNoche != null) && (
                  <div className="cal-detail-grid">
                    {ev.reservaTotalClp != null && (
                      <Field icon={<DollarOutlined />} label="Total">
                        ${Number(ev.reservaTotalClp).toLocaleString("es-CL")}
                      </Field>
                    )}
                    {ev.precioPorNoche != null && (
                      <Field icon={<MoonOutlined />} label="Precio / noche">
                        ${Number(ev.precioPorNoche).toLocaleString("es-CL")}
                      </Field>
                    )}
                  </div>
                )}

                {(ev.huesped || (ev.telefono && !hideGuestPhone)) && (
                  <div className="cal-detail-grid">
                    {ev.huesped && (
                      <Field icon={<UserOutlined />} label="Huésped">
                        {ev.huesped}
                      </Field>
                    )}
                    {ev.telefono && !hideGuestPhone && (
                      <Field icon={<PhoneOutlined />} label="Teléfono">
                        {formatPhoneDisplay(ev.telefono)}
                      </Field>
                    )}
                  </div>
                )}

                {/* El aseo AUTO vinculado ya NO se muestra aquí como línea
                    resumen: el modal inyecta su TARJETA de aseo editable
                    completa más abajo (ver la inyección en `grouped`, round
                    2026-07-11), igual que cuando el aseo comparte el día con
                    el arriendo. */}
              </div>

              <div className="cal-detail-card__actions">
                <Space wrap size={8}>
                  {!hideFinancesAction && (
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => goToFinanzasArriendos(ev)}
                    >
                      Ver en Finanzas
                    </Button>
                  )}
                  {!hideCopyActions && (
                    <>
                      <Button
                        size="small"
                        icon={copiedInfo ? <CheckOutlined /> : <CopyOutlined />}
                        onClick={() => copyInfo(ev)}
                        type={copiedInfo ? "primary" : "default"}
                      >
                        {copiedInfo ? "¡Copiado!" : "Copiar información"}
                      </Button>
                      <Button
                        size="small"
                        icon={
                          copiedInst ? <CheckOutlined /> : <FileTextOutlined />
                        }
                        onClick={() => copyInstructions()}
                        type={copiedInst ? "primary" : "default"}
                      >
                        {copiedInst ? "¡Copiado!" : "Copiar instrucciones"}
                      </Button>
                    </>
                  )}
                  {showActions && onEditEvent && (
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => onEditEvent(ev, slots)}
                    >
                      Editar
                    </Button>
                  )}
                  {/*
                    Botón "Recalcular finanzas": sólo visible si el usuario
                    es admin/superadmin Y la reserva tiene un id de DB
                    (`bookingDbId`) — los bloques/aseos no aplican. Permite
                    re-procesar el último JSON de Stays para que la reserva
                    refleje cambios en subitems de propiedad o en la fórmula
                    financiera del backend. Ver `RecalcBookingButton` y
                    docs/modulo-stays.md §"Recálculo manual desde UI".
                  */}
                  {showActions && ev.bookingDbId != null && (
                    <RecalcBookingButton
                      bookingId={Number(ev.bookingDbId)}
                      userRole={userRole}
                      onRecalculated={onRecalculated}
                    />
                  )}
                  {/* Regenerar SOLO el aseo (acción acotada): recrea el aseo
                      vinculado si fue borrado por error. Reusa la cascada, no
                      toca finanzas. Round 2026-06-22. */}
                  {showActions && ev.bookingDbId != null && (
                    <RegenerarAseoButton
                      bookingId={Number(ev.bookingDbId)}
                      userRole={userRole}
                      onRegenerated={onRecalculated}
                    />
                  )}
                  {showActions && onDeleteEvent && (
                    <BookingDeleteDropdown
                      onDeleteWithCleaning={() =>
                        onDeleteEvent(ev, slots, { preserveCleaning: false })
                      }
                      onDeleteOnly={() =>
                        onDeleteEvent(ev, slots, { preserveCleaning: true })
                      }
                    />
                  )}
                </Space>
              </div>
            </div>
          );
        }

        /* ===============================
           CLEANING — tarjeta cyan con detalles del aseo
           =============================== */
        if (ev.kind === "cleaning") {
          if (hideCleaningDetails) {
            // Vista cliente: SOLO "Aseo" + el Código de estadía AF (para que el
            // cliente cruce este aseo con su arriendo, aunque caigan en días
            // distintos). Sin slot, sin aseador, sin notas, sin urgencia, sin
            // detalles operativos. Ver docs/modulo-calendario.md §"Visibilidad
            // de campos por rol". El código es un identificador NO sensible.
            return (
              <div
                key={`cleaning-${idx}`}
                className="cal-detail-card cal-detail-card--cleaning"
              >
                <div className="cal-detail-card__header">
                  <div className="cal-detail-card__title">
                    <ClearOutlined />
                    <span>Aseo</span>
                  </div>
                  <div className="cal-detail-card__tags">
                    {/* «Aseo #N» = código propio del aseo (aseos.id); «Estadía
                        #N» = arriendo vinculado (cuando existe). */}
                    <AseoCodeTag id={ev.cleaningId} />
                    {ev.reservaId != null && (
                      <EstadiaCodeTag id={ev.reservaId} />
                    )}
                  </div>
                </div>
              </div>
            );
          }
          return (
            <div
              key={`cleaning-${idx}`}
              className="cal-detail-card cal-detail-card--cleaning"
            >
              <div className="cal-detail-card__header">
                <div className="cal-detail-card__title">
                  <ClearOutlined />
                  <span>Aseo</span>
                </div>
                <div className="cal-detail-card__tags">
                  {/* «Aseo #N» = código propio del aseo (aseos.id), único e
                      histórico. Siempre presente. */}
                  <AseoCodeTag id={ev.cleaningId} />
                  {/* Nº de estadía vinculada = MISMA etiqueta que muestra el
                      arriendo ("Estadía #N"). Hace evidente a qué reserva
                      pertenece este aseo, aunque estén en días distintos.
                      Round 2026-07-15. */}
                  <EstadiaCodeTag id={ev.reservaId} />
                  {/* Si el aseo está vinculado a una reserva (reservaId !=
                      null) es un aseo AUTO con rol AF "intermediario": el
                      huésped pagó la limpieza dentro de la reserva, AF
                      pasa el monto al aseador sin ganar utilidad. La
                      etiqueta ayuda a admin (no se muestra en la vista
                      cliente porque entra por la rama hideCleaningDetails). */}
                  {ev.reservaId ? (
                    <Tooltip
                      title={
                        <span>
                          Aseo AUTO — generado por el sistema junto con la
                          reserva. El huésped paga la limpieza completa, que
                          entra a AF. AF la distribuye en 4 flujos:{" "}
                          <b>aseador</b> (pass-through),{" "}
                          <b>lavandería</b> (pass-through, AF o externa según
                          la propiedad), y <b>transporte + materiales</b>{" "}
                          (estos dos los retiene AF como ganancia neta).{" "}
                          <u>SÍ afecta la utilidad AF</u> (positivamente vía
                          transporte + materiales). Ver{" "}
                          <code>docs/flujo-financiero.md §6.5</code>.
                        </span>
                      }
                    >
                      <Tag color="purple" icon={<InfoCircleOutlined />}>
                        Aseo AUTO
                      </Tag>
                    </Tooltip>
                  ) : null}
                  {ev.urgencia ? <Tag color="orange">{ev.urgencia}</Tag> : null}
                  <Tag>{slotLabel(group.slots)}</Tag>
                </div>
              </div>

              <div className="cal-detail-card__body">
                <div className="cal-detail-grid">
                  <Field
                    icon={<ClockCircleOutlined />}
                    label="Inicio"
                    highlight
                  >
                    {fmtDate(ev.start)}
                    <span className="cal-detail-field__meta">
                      · {fmtTime(ev.start)}
                    </span>
                  </Field>
                  <Field
                    icon={<ClockCircleOutlined />}
                    label="Término"
                    highlight
                  >
                    {fmtDate(ev.end)}
                    <span className="cal-detail-field__meta">
                      · {fmtTimeEnd(ev.end)}
                    </span>
                  </Field>
                </div>

                {/* Aseo AUTO vinculado a una reserva: mostramos el huésped
                    para que el admin sepa al toque de qué estadía es este
                    aseo. Si la reserva está en otra propiedad (caso del bug
                    histórico id_propiedad mismatch), el nombre aparece
                    aunque la fila visible sea otra. */}
                {ev.reservaId && (ev as any).huespedReserva && (
                  <Field icon={<HomeOutlined />} label="Reserva de">
                    <span>
                      <strong>{(ev as any).huespedReserva}</strong>
                      <span className="cal-detail-field__meta">
                        {" "}· estadía {formatEstadiaCode(ev.reservaId)}
                      </span>
                    </span>
                  </Field>
                )}

                {ev.aseadorNombre && (
                  <Field icon={<UserOutlined />} label="Aseador">
                    {ev.aseadorNombre}
                  </Field>
                )}

                {ev.notas && (
                  <Field icon={<InfoCircleOutlined />} label="Notas">
                    <span className="cal-detail-multiline">{ev.notas}</span>
                  </Field>
                )}
              </div>

              {showActions ? (
                <div className="cal-detail-card__actions">
                  <Space size={8}>
                    {onEditEvent && (
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => onEditEvent(ev, slotsSorted)}
                      >
                        Editar
                      </Button>
                    )}
                    {onDeleteEvent && (
                      <Popconfirm
                        title="¿Eliminar este aseo?"
                        okText="Eliminar"
                        cancelText="Cancelar"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => onDeleteEvent(ev, slotsSorted)}
                      >
                        <Button
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                        >
                          Eliminar
                        </Button>
                      </Popconfirm>
                    )}
                  </Space>
                </div>
              ) : null}
            </div>
          );
        }

        /* ===============================
           BLOCK / MAINTENANCE — tarjeta principal (el foco del rediseño)
           =============================== */
        const isMaintenance = ev.kind === "maintenance";
        const typeLabel = isMaintenance ? "Mantención" : "Bloqueo";
        const typeIcon = isMaintenance ? <ToolOutlined /> : <LockOutlined />;
        // Tri-estado del creador (modulo-calendario.md §5): true admin ·
        // false cliente · null/undefined webhook de Stays. El binario viejo
        // etiquetaba los de Stays como "Creado por cliente" y les daba la
        // tarjeta clara del cliente (fix 2026-07-02).
        const createdByAdmin = (ev as any).createdByAdmin === true;
        const createdByClient = (ev as any).createdByAdmin === false;
        const authorLabel = createdByAdmin
          ? "Creado por admin"
          : createdByClient
            ? "Creado por cliente"
            : "Desde Stays";
        const authorColor = createdByAdmin
          ? "geekblue"
          : createdByClient
            ? "gold"
            : "default";

        const duration = durationLabel(ev.start, ev.end);
        const cardModifier = isMaintenance
          ? "cal-detail-card--maintenance"
          : "cal-detail-card--block";
        // Solo el creado por CLIENTE lleva la variante clara — igual que la
        // barra (`.is-from-client` ⟺ createdByAdmin === false).
        const fromClientClass = createdByClient
          ? "cal-detail-card--from-client"
          : "";

        return (
          <div
            key={`${ev.kind}-${idx}`}
            className={`cal-detail-card ${cardModifier} ${fromClientClass}`.trim()}
          >
            <div className="cal-detail-card__header">
              <div className="cal-detail-card__title">
                {typeIcon}
                <span>{typeLabel}</span>
              </div>
              <div className="cal-detail-card__tags">
                <Tag color={authorColor}>{authorLabel}</Tag>
                <Tag>{slotLabel(group.slots)}</Tag>
              </div>
            </div>

            <div className="cal-detail-card__body">
              <div className="cal-detail-grid">
                <Field
                  icon={<CalendarOutlined />}
                  label="Check-in"
                  highlight
                >
                  {fmtDate(ev.start)}
                  <span className="cal-detail-field__meta">
                    · {fmtTime(ev.start)}
                  </span>
                </Field>
                <Field
                  icon={<CalendarOutlined />}
                  label="Check-out"
                  highlight
                >
                  {fmtDate(ev.end)}
                  <span className="cal-detail-field__meta">
                    · {fmtTimeEnd(ev.end)}
                  </span>
                </Field>
              </div>

              {duration ? (
                <div className="cal-detail-duration">
                  <MoonOutlined />
                  <span>{duration}</span>
                </div>
              ) : null}

              {ev.razon ? (
                <Field icon={<FileTextOutlined />} label="Razón">
                  <span className="cal-detail-multiline">{ev.razon}</span>
                </Field>
              ) : null}

              {/* Notas internas: visible SOLO para admin. Ver
                  docs/modulo-calendario.md §"Visibilidad de campos por rol". */}
              {!hideInternalNotes && ev.notas ? (
                <Field icon={<InfoCircleOutlined />} label="Notas internas">
                  <span className="cal-detail-multiline">{ev.notas}</span>
                </Field>
              ) : null}
            </div>

            {showActions ? (
              <div className="cal-detail-card__actions">
                <Space size={8}>
                  {onEditEvent && (
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => onEditEvent(ev, slotsSorted)}
                    >
                      Editar
                    </Button>
                  )}
                  {onDeleteEvent && (
                    <Popconfirm
                      title={`¿Eliminar ${isMaintenance ? "esta mantención" : "este bloqueo"}?`}
                      description="Esta acción no se puede deshacer."
                      okText="Eliminar"
                      cancelText="Cancelar"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => onDeleteEvent(ev, slotsSorted)}
                    >
                      <Button size="small" danger icon={<DeleteOutlined />}>
                        Eliminar
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              </div>
            ) : null}
          </div>
        );
      })}

      {/*
       * Panel informativo admin: explica cómo se comporta el calendario al
       * editar un arriendo. Oculto para cliente (usamos hideCopyActions
       * como proxy de "vista cliente" — ver prop doc). Colapsable para no
       * saturar la UI.
       * Fuente canónica: docs/calendario-cascada-arriendos.md §1-6 y
       * docs/calendario-logica.md §1, §5.
       */}
      {!hideCopyActions ? (
        <details style={{ marginTop: 16 }}>
          <summary
            style={{
              cursor: "pointer",
              fontSize: 13,
              color: "var(--text-secondary, #6b7280)",
              userSelect: "none",
            }}
          >
            <InfoCircleOutlined style={{ marginRight: 6 }} />
            Cómo funciona el calendario (horarios, extensiones, salida tardía,
            eliminación)
          </summary>
          <div
            style={{
              marginTop: 8,
              padding: 12,
              background: "var(--surface-subtle, #f8fafc)",
              borderRadius: 6,
              fontSize: 12.5,
              lineHeight: 1.55,
            }}
          >
            <p style={{ marginTop: 0 }}>
              <strong>Convención horaria (3 tercios por día)</strong>
            </p>
            <ul style={{ marginTop: 0, paddingLeft: 18 }}>
              <li>
                <strong>Mañana</strong> 00:00 – 10:59 · <strong>Mediodía</strong>{" "}
                11:00 – 14:59 · <strong>Tarde/Noche</strong> 15:00 – 23:59.
              </li>
              <li>
                Arriendo estándar: entra 15:00 del día de llegada, sale{" "}
                <strong>10:59</strong> del día de salida (no 11:00 — ese minuto
                ya pertenece al aseo).
              </li>
              <li>
                Aseo AUTO: SIEMPRE en el tercio <strong>mediodía (11:00)</strong>{" "}
                del día del check-out.
              </li>
            </ul>
            <p style={{ marginTop: 8 }}>
              <strong>Extender noches</strong>
            </p>
            <ul style={{ marginTop: 0, paddingLeft: 18 }}>
              <li>
                La <code>salida</code> se corre +N días. El <strong>aseo AUTO
                se mueve solo</strong> al nuevo día de salida (tercio
                mediodía).
              </li>
              <li>
                Si el tercio destino del aseo está ocupado por otro arriendo,
                el sistema rechaza y hay que resolver el conflicto primero.
              </li>
            </ul>
            <p style={{ marginTop: 8 }}>
              <strong>Salida tardía</strong>
            </p>
            <ul style={{ marginTop: 0, paddingLeft: 18 }}>
              <li>
                Setear <code>hora_nueva</code> (ej. 14:00) estira el arriendo
                hasta esa hora y <strong>crea un bloqueo AUTO</strong> desde
                ahí hasta 10:59 del día siguiente.
              </li>
              <li>
                El aseo AUTO se corre al <strong>día siguiente</strong>{" "}
                (11:00 mediodía) — el bloqueo cubre el hueco.
              </li>
              <li>
                Quitar la salida tardía borra el bloqueo AUTO y devuelve el
                aseo al día del check-out.
              </li>
            </ul>
            <p style={{ marginTop: 8 }}>
              <strong>Modificar inicio/fin del arriendo</strong>
            </p>
            <ul style={{ marginTop: 0, paddingLeft: 18 }}>
              <li>
                Cualquier cambio de fechas dispara overlap check contra todos
                los arriendos de la propiedad en los tercios afectados.
              </li>
              <li>
                Arriendos consecutivos pueden compartir día (uno sale 10:59,
                el otro entra 15:00) — el tercio <em>mediodía</em> queda para
                el aseo AUTO.
              </li>
              <li>
                Un arriendo NO puede pisar ningún tercio ya ocupado por OTRO
                arriendo. Aseos y bloqueos SÍ pueden apilarse (se renderizan
                en otra fila).
              </li>
            </ul>
            <p style={{ marginTop: 8 }}>
              <strong>Eliminar arriendo</strong>
            </p>
            <ul style={{ marginTop: 0, paddingLeft: 18 }}>
              <li>
                <strong>"Eliminar arriendo y aseo"</strong> (default): borra
                la reserva, el aseo AUTO, el bloqueo de salida tardía y los
                cargos adicionales.
              </li>
              <li>
                <strong>"Eliminar solo arriendo (conservar aseo)"</strong>:
                borra la reserva, el bloqueo y los cargos; el aseo queda como
                evento independiente (sin <code>reserva_id</code>) — útil si
                ya se ejecutó o tiene aseador asignado.
              </li>
            </ul>
          </div>
        </details>
      ) : null}
    </Modal>
  );
}
